import AddressAutocomplete from '../../shared/AddressAutocomplete';
import TimeCombo from '../foundation/TimeCombo';
import '../foundation/eventsFoundation.css';
import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import EscapeGuard from '../guests/EscapeGuard';
import { DEFAULT_EVENT_WORDS } from '../eventWords';
import { ADDRESS_MAX, NOTES_MAX, PASSENGERS_MAX, transportForm, transportProblem } from './guestTransport';

const SELECT_CLASS = 'mt-[3px] w-full min-h-[44px] lg:[@media(pointer:fine)]:min-h-[34px] rounded-lg border border-[#64748B] bg-[#0B1220] px-2 text-white';

// Module 19 (BL-380) G9a - request one shuttle trip for the event. The platform popup (Create-reservation look); it holds typed text, so
// Escape never discards it (M6 rule); Cancel and the x still close. The trip goes to Dispatch as "requested": Dispatch confirms the price.
export default function GuestTransportPopup({ context, saving = false, error = '', onSave, onClose, words = DEFAULT_EVENT_WORDS }) {
    const [form, setForm] = useState(() => transportForm(context));
    const [problem, setProblem] = useState('');
    const set = (name) => (event) => setForm((previous) => ({ ...previous, [name]: event.target.value }));
    const companies = context?.companies || [];
    const services = context?.services || [];

    const submit = (event) => {
        event?.preventDefault?.();
        const message = transportProblem(form);
        if (message) return setProblem(message);
        setProblem('');
        onSave(form);
    };
    const field = (id, label, control, { wide = false, hint = '' } = {}) => (
        <li className={`ia-resv-field${wide ? ' ia-resv-field--wide' : ''}`}>
            <div className="ia-resv-field__label"><label htmlFor={id}>{label}</label></div>
            {control}
            {hint && <p className="ia-resv-field__why">{hint}</p>}
        </li>
    );
    const shown = problem || error;
    const guests = Number(context?.shuttleGuests) || 0;

    return (
        <EscapeGuard>
            <Modal3D
                portal
                size="wide"
                title="Request a shuttle trip"
                subtitle={`Sent to Dispatch as a requested trip for this ${words.eventLower}. Dispatch confirms the price and the driver; nothing is sent to guests.`}
                testId="guest-transport-popup"
                onClose={onClose}
                data-m6-layout="modals"
                data-m6-component="EventGuestTransportPopup"
                actions={(
                    <>
                        <Button3D onClick={onClose} disabled={saving}>Cancel</Button3D>
                        <Button3D variant="go" onClick={submit} disabled={saving}>{saving ? 'Requesting…' : 'Request trip'}</Button3D>
                    </>
                )}
            >
                <form onSubmit={submit} noValidate>
                    <ul className="ia-resv-grid" aria-label="Shuttle trip">
                        {companies.length > 1 && field('transport-company', 'Company', (
                            <select id="transport-company" className={SELECT_CLASS} value={form.companyId} onChange={set('companyId')}>
                                {companies.map((company) => <option key={company.id} value={company.id}>{company.label}</option>)}
                            </select>
                        ), { wide: true })}
                        {field('transport-service', 'Service', (
                            <select id="transport-service" className={SELECT_CLASS} value={form.serviceId} onChange={set('serviceId')}>
                                <option value="">Choose a service…</option>
                                {services.map((service) => <option key={service.id} value={service.id}>{service.name}</option>)}
                            </select>
                        ), { wide: true })}
                        {field('transport-pickup', 'Pickup', <AddressAutocomplete id="transport-pickup" module="events" label="Pickup" maxLength={ADDRESS_MAX} value={form.pickup} onChange={(pickup) => setForm((previous) => ({ ...previous, pickup }))} placeholder="e.g. Hartsfield-Jackson Airport, North Terminal" />, { wide: true })}
                        {field('transport-dropoff', 'Drop-off', <AddressAutocomplete id="transport-dropoff" module="events" label="Drop-off" maxLength={ADDRESS_MAX} value={form.dropoff} onChange={(dropoff) => setForm((previous) => ({ ...previous, dropoff }))} />, { wide: true, hint: context?.defaultDropoff ? 'Filled in from the hotel block or the venue. Change it if this trip goes somewhere else.' : '' })}
                        {field('transport-date', 'Pickup date', <input id="transport-date" type="date" value={form.date} onChange={set('date')} />)}
                        {field('transport-time', 'Pickup time', <TimeCombo id="transport-time" label="Pickup time" value={form.time} onChange={(time) => setForm((previous) => ({ ...previous, time }))} />, { hint: context?.timeZone ? `Company time (${context.timeZone}).` : '' })}
                        {field('transport-passengers', 'Passengers', <input id="transport-passengers" type="number" inputMode="numeric" min={1} max={PASSENGERS_MAX} value={form.passengers} onChange={set('passengers')} />, { hint: `${guests} ${guests === 1 ? 'guest needs' : 'guests need'} the shuttle, plus-ones included.` })}
                        {field('transport-notes', 'Notes for Dispatch', <textarea id="transport-notes" rows={2} maxLength={NOTES_MAX} value={form.notes} onChange={set('notes')} />, { wide: true })}
                    </ul>
                    {shown && <p className="ia-resv-error" role="alert">{shown}</p>}
                </form>
            </Modal3D>
        </EscapeGuard>
    );
}
